import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Send, Plus, Search, Edit, Trash2, X, Check, AlertCircle,
  Mail, Clock, UserPlus, RefreshCw, Eye, ChevronLeft, ChevronRight,
  Users, CheckCircle, XCircle, Timer, ArrowRight, Filter,
  Calendar, Shield, Link2, ExternalLink, Download, Key
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import invitationService from '../services/invitationService';
import roleService from '../services/roleService';
import tagService from '../services/tagService';
import permissionService from '../services/permissionService';
import ResponsiveDataTable from '../components/ResponsiveDataTable';
import EnhancedModal from '../components/EnhancedModal';
import { 
  ResponsiveForm, 
  ResponsiveInput, 
  ResponsiveButton,
  ResponsiveSelect
} from '../components/ResponsiveModal';
import CommonLoader, { PageLoader } from '../components/CommonLoader';
import SearchableSelect from '../components/SearchableSelect';
import MultiSelectSearchable from '../components/MultiSelectSearchable';
import { validateEmail, validateRequired, ErrorMessage } from '../utils/validation';
import FilterBar from '../components/FilterBar';
import StandardDataTable from '../components/StandardDataTable';
import { CreateButton } from '../components/CommonButtons';
import EnhancedDataTable from '../components/EnhancedDataTable';

const Invitations = () => {
  const { dataroomUuid } = useParams();
  const navigate = useNavigate();
  
  // Add error handling for missing dataroomUuid
  useEffect(() => {
    if (!dataroomUuid) {
      toast.error('Invalid dataroom');
      navigate('/datarooms');
    }
  }, [dataroomUuid, navigate]);
  const [invitations, setInvitations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectionType, setSelectionType] = useState('permission'); // 'role' or 'permission'
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  });

  // Statistics - Commented out as counts are hidden
  // const [stats, setStats] = useState({
  //   total: 0,
  //   pending: 0,
  //   accepted: 0,
  //   rejected: 0,
  //   expired: 0,
  //   acceptanceRate: 0
  // });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    dataroom_role_id: '',
    permission_ids: [],
    tag_id: '',
    message: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (dataroomUuid) {
      fetchInvitations();
      fetchRoles();
      fetchPermissions();
      fetchTags();
    }
  }, [dataroomUuid, pagination.current_page, searchTerm, filterStatus, selectedRole]);

  // Calculate stats whenever invitations change - Commented out as counts are hidden
  // useEffect(() => {
  //   calculateStats();
  // }, [invitations]);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(filterStatus !== '' && { status: parseInt(filterStatus) }),
        ...(selectedRole && { dataroom_role_id: selectedRole })
      };

      const response = await invitationService.list(dataroomUuid, params);
      
      // Use normalized response structure
      setInvitations(response.data || []);
      
      // Handle pagination from normalized response
      if (response.pagination) {
        setPagination({
          current_page: response.pagination.current_page || 1,
          per_page: response.pagination.per_page || 15,
          total: response.pagination.total || 0,
          last_page: response.pagination.last_page || 1
        });
      }
    } catch (error) {
      
      // Show specific error message
      if (error.response) {
        if (error.response.status === 404) {
          toast.error('Dataroom not found');
        } else if (error.response.status === 403) {
          toast.error('You do not have permission to view invitations for this dataroom');
        } else {
          toast.error(error.response.data?.message || 'Failed to fetch invitations');
        }
      } else if (error.request) {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('Failed to fetch invitations');
      }
      
      // Use sample data for demonstration
      const sampleInvitations = [
        {
          id: 1,
          email: 'john.doe@example.com',
          role: { id: 1, display_name: 'Viewer', name: 'viewer' },
          status: 'pending',
          created_at: '2024-01-15T10:00:00Z',
          expires_at: '2024-02-15T10:00:00Z',
          invitation_link: 'https://lockbox.com/invite/abc123'
        },
        {
          id: 2,
          email: 'jane.smith@example.com',
          role: { id: 2, display_name: 'Editor', name: 'editor' },
          status: 'accepted',
          created_at: '2024-01-10T10:00:00Z',
          accepted_at: '2024-01-11T14:30:00Z'
        },
        {
          id: 3,
          email: 'mike.wilson@example.com',
          role: { id: 1, display_name: 'Viewer', name: 'viewer' },
          status: 'expired',
          created_at: '2023-12-01T10:00:00Z',
          expires_at: '2024-01-01T10:00:00Z'
        }
      ];
      setInvitations(sampleInvitations);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      
      // Use the correct API endpoint for dataroom roles (user side endpoint)
      const response = await api.get(`/datarooms/${dataroomUuid}/roles`, {
        params: {
          per_page: 50,
          page: 1
        }
      });
      
      
      // Handle different response structures
      let rolesData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          rolesData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          rolesData = response.data.data;
        } else if (response.data.roles && Array.isArray(response.data.roles)) {
          rolesData = response.data.roles;
        }
      }
      
      
      // If no roles found, use default roles
      if (rolesData.length === 0) {
        const defaultRoles = [
          { id: 1, name: 'viewer', display_name: 'Viewer', description: 'Can view dataroom contents' },
          { id: 2, name: 'editor', display_name: 'Editor', description: 'Can view and edit dataroom contents' },
          { id: 3, name: 'admin', display_name: 'Admin', description: 'Can manage dataroom settings' },
          { id: 4, name: 'owner', display_name: 'Owner', description: 'Full control over dataroom' }
        ];
        setRoles(defaultRoles);
      } else {
        setRoles(rolesData);
      }
    } catch (error) {
      // Use default roles if API fails
      const defaultRoles = [
        { id: 1, name: 'viewer', display_name: 'Viewer', description: 'Can view dataroom contents' },
        { id: 2, name: 'editor', display_name: 'Editor', description: 'Can view and edit dataroom contents' },
        { id: 3, name: 'admin', display_name: 'Admin', description: 'Can manage dataroom settings' },
        { id: 4, name: 'owner', display_name: 'Owner', description: 'Full control over dataroom' }
      ];
      setRoles(defaultRoles);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await permissionService.list();
      const permissionsData = response.data || [];
      setPermissions(permissionsData);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions([]);
    }
  };

  const fetchTags = async () => {
    try {
      
      // Use the tagService to get invitation tags
      const response = await tagService.getInvitationTags(dataroomUuid);
      
      // The normalized response should have data array
      const tagsData = response.data || [];
      
      
      setTags(tagsData);
    } catch (error) {
      
      // If API fails, set empty array - no tags available
      setTags([]);
    }
  };

  // Stats calculation function - Commented out as counts are hidden
  // const calculateStats = () => {
  //   // Ensure invitations is an array before calculating stats
  //   const invitationsArray = Array.isArray(invitations) ? invitations : [];
  //   
  //   const total = invitationsArray.length;
  //   const pending = invitationsArray.filter(inv => inv.status === 'pending').length;
  //   const accepted = invitationsArray.filter(inv => inv.status === 'accepted').length;
  //   const rejected = invitationsArray.filter(inv => inv.status === 'rejected').length;
  //   const expired = invitationsArray.filter(inv => inv.status === 'expired').length;
  //   const acceptanceRate = total > 0 ? Math.round((accepted / total) * 100) : 0;
  //
  //   setStats({
  //     total,
  //     pending,
  //     accepted,
  //     rejected,
  //     expired,
  //     acceptanceRate
  //   });
  // };

  const validateForm = () => {
    const errors = {};
    
    // Validate email
    const emailError = validateEmail(formData.email);
    if (emailError) {
      errors.email = emailError;
    }

    // Validate role or permissions based on selection type
    if (selectionType === 'role') {
      const roleError = validateRequired(formData.dataroom_role_id, 'Role');
      if (roleError) {
        errors.dataroom_role_id = roleError;
      }
    } else {
      if (!formData.permission_ids || formData.permission_ids.length === 0) {
        errors.permission_ids = 'At least one permission is required';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      // Prepare data based on selection type
      const dataToSend = {
        email: formData.email
      };

      // Add tag if selected
      if (formData.tag_id) {
        dataToSend.tag_id = formData.tag_id;
      }

      // Add message if provided
      if (formData.message) {
        dataToSend.message = formData.message;
      }

      if (selectionType === 'role') {
        dataToSend.dataroom_role_id = formData.dataroom_role_id;
      } else {
        dataToSend.permission_ids = formData.permission_ids;
      }

      await invitationService.create(dataroomUuid, dataToSend);
      toast.success('Invitation sent successfully');
      setShowCreateModal(false);
      resetForm();
      fetchInvitations();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send invitation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async (invitation) => {
    try {
      await invitationService.resend(dataroomUuid, invitation.id);
      toast.success('Invitation resent successfully');
      fetchInvitations();
    } catch (error) {
      toast.error('Failed to resend invitation');
    }
  };

  // Copy link functionality removed as requested
  // const handleCopyLink = async (invitation) => {
  //   if (invitation.invitation_link) {
  //     try {
  //       await navigator.clipboard.writeText(invitation.invitation_link);
  //       toast.success('Invitation link copied to clipboard');
  //     } catch (error) {
  //       // Fallback for browsers that don't support clipboard API
  //       const textArea = document.createElement('textarea');
  //       textArea.value = invitation.invitation_link;
  //       textArea.style.position = 'fixed';
  //       textArea.style.left = '-999999px';
  //       document.body.appendChild(textArea);
  //       textArea.focus();
  //       textArea.select();
  //       try {
  //         document.execCommand('copy');
  //         toast.success('Invitation link copied to clipboard');
  //       } catch (err) {
  //         toast.error('Failed to copy invitation link');
  //       }
  //       document.body.removeChild(textArea);
  //     }
  //   } else {
  //     toast.error('No invitation link available');
  //   }
  // };

  const handleDelete = async () => {
    try {
      setIsSubmitting(true);
      await invitationService.delete(dataroomUuid, selectedInvitation.id);
      toast.success('Invitation deleted successfully');
      setShowDeleteModal(false);
      setSelectedInvitation(null);
      fetchInvitations();
    } catch (error) {
      toast.error('Failed to delete invitation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      dataroom_role_id: '',
      permission_ids: [],
      tag_id: '',
      message: ''
    });
    setFormErrors({});
    setSelectionType('role');
  };

  const getStatusBadge = (status) => {
    // Handle both numeric and string status values
    let statusKey = status;
    
    // Convert numeric status to string key
    if (typeof status === 'number' || !isNaN(status)) {
      const numStatus = parseInt(status);
      if (numStatus === 0) {
        statusKey = 'inactive';
      } else if (numStatus === 1) {
        statusKey = 'active';
      } else if (numStatus === 2) {
        statusKey = 'pending';
      } else {
        statusKey = 'pending';
      }
    }
    
    const statusConfig = {
      pending: {
        color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
        icon: Clock,
        label: 'Pending'
      },
      active: {
        color: 'bg-green-50 text-green-700 border-green-200',
        icon: CheckCircle,
        label: 'Active'
      },
      accepted: {
        color: 'bg-green-50 text-green-700 border-green-200',
        icon: CheckCircle,
        label: 'Accepted'
      },
      inactive: {
        color: 'bg-gray-50 text-gray-700 border-gray-200',
        icon: XCircle,
        label: 'Not Active'
      },
      rejected: {
        color: 'bg-red-50 text-red-700 border-red-200',
        icon: XCircle,
        label: 'Rejected'
      },
      expired: {
        color: 'bg-gray-50 text-gray-700 border-gray-200',
        icon: Timer,
        label: 'Expired'
      }
    };
    
    const config = statusConfig[statusKey] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const columns = [
    {
      key: 'recipient',
      header: 'Recipient',
      sortable: false,
      render: (value, row) => {
        if (!row) return <span className="text-gray-500">-</span>;
        return (
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold">
                {row.email ? row.email.charAt(0).toUpperCase() : '?'}
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">
                {row.email || 'No email'}
              </p>
              <p className="text-xs text-gray-500 flex items-center mt-1">
                <Shield className="w-3 h-3 mr-1" />
                {row.role?.display_name || row.role?.name || 'N/A'}
              </p>
            </div>
          </div>
        );
      }
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (value, row) => {
        if (!row) return <span className="text-gray-500">-</span>;
        return (
          <div className="flex flex-col items-start">
            {getStatusBadge(row.status || 'pending')}
            {row.status === 'accepted' && row.accepted_at && (
              <span className="text-xs text-gray-500 mt-1">
                Accepted {new Date(row.accepted_at).toLocaleDateString()}
              </span>
            )}
          </div>
        );
      }
    },
    {
      key: 'created_at',
      header: 'Sent',
      sortable: true,
      render: (value, row) => {
        if (!row) return <span className="text-gray-500">-</span>;
        return (
          <div className="text-sm">
            <p className="text-gray-900">
              {row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A'}
            </p>
            <p className="text-xs text-gray-500">
              {row.created_at ? new Date(row.created_at).toLocaleTimeString() : ''}
            </p>
          </div>
        );
      }
    },
    {
      key: 'expires_at',
      header: 'Expires',
      sortable: true,
      render: (value, row) => {
        if (!row) return <span className="text-gray-500">-</span>;
        if (!row.expires_at) {
          return <span className="text-sm text-gray-500">Never</span>;
        }
        
        const expiryDate = new Date(row.expires_at);
        const now = new Date();
        const daysLeft = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
        
        return (
          <div className="text-sm">
            <p className="text-gray-900">
              {expiryDate.toLocaleDateString()}
            </p>
            {row.status === 'pending' && daysLeft > 0 && (
              <p className={`text-xs ${daysLeft <= 7 ? 'text-orange-600' : 'text-gray-500'}`}>
                {daysLeft} days left
              </p>
            )}
          </div>
        );
      }
    }
  ];

  const actions = (row) => {
    if (!row) return [];
    return [
    {
      icon: Eye,
      label: 'View Details',
      onClick: () => {
        setSelectedInvitation(row);
        setShowViewModal(true);
      }
    },
    {
      icon: RefreshCw,
      label: 'Resend',
      onClick: () => handleResend(row),
      disabled: row.status !== 'pending' && row.status !== 2 && row.status !== '2' && row.status !== 0 && row.status !== '0'
    },
    {
      icon: Trash2,
      label: 'Delete',
      onClick: () => {
        setSelectedInvitation(row);
        setShowDeleteModal(true);
      },
      className: 'text-red-600 hover:text-red-800'
    }
  ];
  };

  if (!dataroomUuid) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center"
          >
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              No Dataroom Selected
            </h2>
            <p className="text-gray-600 mb-6">
              Please select a dataroom from the Datarooms page to manage invitations.
            </p>
            <button
              onClick={() => navigate('/datarooms')}
              className="inline-flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Go to Datarooms
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-4">
      {/* Page Header */}
      <div className="bg-white border-b px-3 sm:px-3 py-1.5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Invitations Management</h2>
            <p className="mt-1 text-sm text-gray-600">
              Manage invitations and track user access for this dataroom
            </p>
          </div>
          <CreateButton
            onClick={() => setShowCreateModal(true)}
            icon={Send}
          >
            Send Invitation
          </CreateButton>
        </div>
      </div>

      {/* Content Card */}
      <div className="bg-white px-3 sm:px-3 py-1.5">

        {/* Statistics Cards - Hidden as per requirement */}
        {/* <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Total</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Pending</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.pending}</p>
              </div>
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Accepted</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stats.accepted}</p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Rejected</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{stats.rejected}</p>
              </div>
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Expired</p>
                <p className="text-2xl font-bold text-gray-600 mt-1">{stats.expired}</p>
              </div>
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <Timer className="w-5 h-5 text-gray-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-primary-600 rounded-xl p-4 shadow-sm text-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-primary-100 uppercase tracking-wider">Accept Rate</p>
                <p className="text-2xl font-bold mt-1">{stats.acceptanceRate}%</p>
              </div>
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <UserPlus className="w-5 h-5" />
              </div>
            </div>
          </motion.div>
        </div> */}

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
          <div className="flex flex-1 gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="">All Status</option>
              <option value="0">Not Active</option>
              <option value="1">Active</option>
              <option value="2">Pending</option>
            </select>

            {/* Role Filter */}
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="">All Roles</option>
              {Array.isArray(roles) && roles.length > 0 ? (
                roles.map(role => (
                  <option key={role.id || role.name} value={role.id}>
                    {role.display_name || role.name || 'Unnamed Role'}
                  </option>
                ))
              ) : (
                <option value="" disabled>No roles available</option>
              )}
            </select>
          </div>
        </div>

        {/* Invitations Table */}
        {loading ? (
          <PageLoader />
        ) : (
          <>
            <EnhancedDataTable
              data={Array.isArray(invitations) ? invitations : []}
              columns={columns}
              actions={actions}
              emptyMessage="No invitations found"
              emptyIcon={Send}
              showColumnControls={true}
              enableColumnResize={true}
              enableDragDrop={true}
              onSort={(key, direction) => {
                const sorted = [...invitations].sort((a, b) => {
                  let aVal = a[key];
                  let bVal = b[key];
                  
                  if (key === 'created_at' || key === 'expires_at') {
                    aVal = aVal ? new Date(aVal).getTime() : 0;
                    bVal = bVal ? new Date(bVal).getTime() : 0;
                  }
                  
                  if (aVal < bVal) return direction === 'asc' ? -1 : 1;
                  if (aVal > bVal) return direction === 'asc' ? 1 : -1;
                  return 0;
                });
                setInvitations(sorted);
              }}
            />
            
            {/* Pagination */}
            {pagination.last_page > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-700">
                    Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                    {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                    {pagination.total} results
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                      disabled={pagination.current_page === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 hover:bg-gray-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    {[...Array(pagination.last_page)].map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => setPagination(prev => ({ ...prev, current_page: i + 1 }))}
                        className={`px-3 py-1 rounded-md ${
                          pagination.current_page === i + 1
                            ? 'bg-primary-600 text-white'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                      disabled={pagination.current_page === pagination.last_page}
                      className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 hover:bg-gray-50"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>

    {/* Create Invitation Modal */}
    <EnhancedModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          title="Send Invitation"
          icon={Send}
          size="large"
          closeOnEscape={true}
          closeOnOutsideClick={true}
          showCloseButton={true}
        >
          <ResponsiveForm onSubmit={handleCreate}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value.toLowerCase() });
                  // Clear error on change
                  if (formErrors.email) {
                    setFormErrors({ ...formErrors, email: '' });
                  }
                }}
                className={`w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-2 ${
                  formErrors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="user@example.com"
              />
              {formErrors.email && <ErrorMessage error={formErrors.email} />}
            </div>
            
            {/* Selection Type Toggle */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Access Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className={`
                  relative flex items-center p-2.5 rounded-lg border-2 cursor-pointer transition-all
                  ${selectionType === 'permission'
                    ? 'border-primary-500 bg-primary-50 shadow-sm'
                    : 'border-gray-300 bg-white hover:border-gray-400'}
                `}>
                  <input
                    type="radio"
                    value="permission"
                    checked={selectionType === 'permission'}
                    onChange={(e) => {
                      setSelectionType(e.target.value);
                      // Clear the other field when switching
                      setFormData({ ...formData, dataroom_role_id: '' });
                      setFormErrors({});
                    }}
                    className="sr-only"
                  />
                  {selectionType === 'permission' && (
                    <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-primary-600" />
                  )}
                  <div className="flex items-center flex-1">
                    <div className={`p-1.5 rounded mr-2 ${selectionType === 'permission' ? 'bg-primary-100' : 'bg-gray-100'}`}>
                      <Key className={`w-4 h-4 ${selectionType === 'permission' ? 'text-primary-600' : 'text-gray-500'}`} />
                    </div>
                    <span className={`font-medium text-xs ${selectionType === 'permission' ? 'text-primary-900' : 'text-gray-700'}`}>
                      Custom Permissions
                    </span>
                  </div>
                </label>

                <label className={`
                  relative flex items-center p-2.5 rounded-lg border-2 cursor-pointer transition-all
                  ${selectionType === 'role'
                    ? 'border-primary-500 bg-primary-50 shadow-sm'
                    : 'border-gray-300 bg-white hover:border-gray-400'}
                `}>
                  <input
                    type="radio"
                    value="role"
                    checked={selectionType === 'role'}
                    onChange={(e) => {
                      setSelectionType(e.target.value);
                      // Clear the other field when switching
                      setFormData({ ...formData, permission_ids: [] });
                      setFormErrors({});
                    }}
                    className="sr-only"
                  />
                  {selectionType === 'role' && (
                    <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-primary-600" />
                  )}
                  <div className="flex items-center flex-1">
                    <div className={`p-1.5 rounded mr-2 ${selectionType === 'role' ? 'bg-primary-100' : 'bg-gray-100'}`}>
                      <Shield className={`w-4 h-4 ${selectionType === 'role' ? 'text-primary-600' : 'text-gray-500'}`} />
                    </div>
                    <span className={`font-medium text-xs ${selectionType === 'role' ? 'text-primary-900' : 'text-gray-700'}`}>
                      Predefined Role
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {/* Role Selection - Only show when role is selected */}
            {selectionType === 'role' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role <span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  value={formData.dataroom_role_id}
                  onChange={(e) => {
                    setFormData({ ...formData, dataroom_role_id: e.target.value });
                    // Clear error on change
                    if (formErrors.dataroom_role_id) {
                      setFormErrors({ ...formErrors, dataroom_role_id: '' });
                    }
                  }}
                  options={roles.map(role => ({
                    ...role,
                    display_name: role.display_name || role.name,
                    description: role.description || `${role.display_name || role.name} role`
                  }))}
                  optionLabel="display_name"
                  optionValue="id"
                  placeholder="Select a role"
                  searchPlaceholder="Search roles..."
                  className={formErrors.dataroom_role_id ? 'error' : ''}
                />
                {formErrors.dataroom_role_id && <ErrorMessage error={formErrors.dataroom_role_id} />}
              </div>
            )}

            {/* Permission Selection - Only show when permission is selected */}
            {selectionType === 'permission' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Permissions <span className="text-red-500">*</span>
                </label>
                <MultiSelectSearchable
                  value={formData.permission_ids}
                  onChange={(e) => {
                    setFormData({ ...formData, permission_ids: e.target.value });
                    // Clear error on change
                    if (formErrors.permission_ids) {
                      setFormErrors({ ...formErrors, permission_ids: '' });
                    }
                  }}
                  options={permissions.map(perm => ({
                    ...perm,
                    display_name: perm.display_name || perm.name,
                    description: perm.description || `${perm.display_name || perm.name} permission`
                  }))}
                  optionLabel="display_name"
                  optionValue="id"
                  optionDescription="description"
                  placeholder="Select permissions"
                  searchPlaceholder="Search permissions..."
                  className={formErrors.permission_ids ? 'error' : ''}
                />
                {formErrors.permission_ids && <ErrorMessage error={formErrors.permission_ids} />}
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags (Optional)
              </label>
              <SearchableSelect
                value={formData.tag_id}
                onChange={(e) => setFormData(prev => ({ ...prev, tag_id: e.target.value }))}
                options={tags}
                optionLabel="name"
                optionValue="id"
                placeholder="Select a tag"
                searchPlaceholder="Search tags..."
              />
            </div>
            
             
            
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 text-sm"
              >
                {isSubmitting ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </ResponsiveForm>
        </EnhancedModal>

        {/* View Invitation Modal */}
        <EnhancedModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedInvitation(null);
          }}
          title="Invitation Details"
          icon={Eye}
          size="large"
          closeOnEscape={true}
          closeOnOutsideClick={true}
          showCloseButton={true}
        >
          {selectedInvitation && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-1 md:col-span-2">
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Email</label>
                    <p className="font-medium text-gray-900 mt-1 break-all">{selectedInvitation.email}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Role</label>
                    <p className="font-medium text-gray-900 mt-1">
                      {selectedInvitation.role?.display_name || selectedInvitation.role?.name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Status</label>
                    <div className="mt-1">{getStatusBadge(selectedInvitation.status || 'pending')}</div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Sent At</label>
                    <p className="font-medium text-gray-900 mt-1">
                      {new Date(selectedInvitation.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Expires At</label>
                    <p className="font-medium text-gray-900 mt-1">
                      {selectedInvitation.expires_at ? new Date(selectedInvitation.expires_at).toLocaleString() : 'Never'}
                    </p>
                  </div>
                </div>
              </div>

              {selectedInvitation.invitation_link && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Invitation Link</label>
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="text"
                      value={selectedInvitation.invitation_link}
                      readOnly
                      className="w-full border border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 py-1.5 rounded-lg px-3 flex-1 bg-gray-50 text-sm"
                    />
                    <a
                      href={selectedInvitation.invitation_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              )}

              {selectedInvitation.message && (
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Message</label>
                  <p className="font-medium text-gray-900 mt-1 bg-gray-50 p-3 rounded-lg">
                    {selectedInvitation.message}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => handleResend(selectedInvitation)}
                  disabled={selectedInvitation.status !== 'pending' && selectedInvitation.status !== 2 && selectedInvitation.status !== '2' && selectedInvitation.status !== 0 && selectedInvitation.status !== '0'}
                  className="inline-flex items-center px-3 py-1.5 text-sm bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Resend Invitation
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedInvitation(null);
                  }}
                  className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200 border border-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </EnhancedModal>

        {/* Delete Confirmation Modal */}
        <EnhancedModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedInvitation(null);
          }}
          title="Delete Invitation"
          icon={AlertCircle}
          iconColor="text-red-600"
          size="medium"
          closeOnEscape={true}
          closeOnOutsideClick={true}
          showCloseButton={true}
        >
          <p className="text-gray-600 mb-6">
            Are you sure you want to delete this invitation? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedInvitation(null);
              }}
              className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200 border border-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={isSubmitting}
              className="inline-flex items-center bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md px-3 py-1.5 text-sm"
            >
              {isSubmitting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </EnhancedModal>
    </>
  );
};

export default Invitations;